import { ReadonlyVec2, ReadonlyVec4, vec2, vec3 } from 'gl-matrix';
import { SignedDistanceField } from '../../core/models/SignedDistanceField';
import { CustomTargetObject } from '../../data/common/customs/CustomTargetObject';
import { ContourTexture } from '../../data/structure/ContourTexture';

import { ImageObject } from '~/engine/data/image/ImageObject';
import { BoundingBox } from '../../data/common/BoundingBox';
import { Grid } from '~/engine/data/common/Grid';
import { gridVoxelBounds } from '~/domain/services/imageService/gridGeometry';

/**
 * {@link DrawObject} is a {@link CustomTargetObject} used as a drawing
 * buffer for contouring tools. Its grid is always a sub-region of the
 * target image grid so that voxel boundaries (especially slice centers)
 * align exactly with the image. The viewport bounding box only determines
 * which portion of the image grid to cover.
 */
export class DrawObject extends CustomTargetObject {
    /**
     * The {@link ReadonlyVec2} describing the size of this {@link DrawObject} in pixels along x and y -axes.
     */
    protected readonly _planarPixelSize: vec2;

    /**
     * Constructs a DrawObject whose grid is a sub-region of the target
     * image grid, covering the visible viewport area. The grid is snapped
     * to image voxel boundaries and sampled at the image resolution, so
     * its voxels are image voxels whichever grid axis the view looks along.
     *
     * @param target Image whose grid defines voxel alignment.
     * @param viewportBoundingBox Visible area in patient space; used only
     *        to determine which image voxels to cover.
     * @param color Outline color for rendering.
     */
    public constructor(
        target: ImageObject,
        viewportBoundingBox: BoundingBox,
        color: ReadonlyVec4
    ) {
        super(target);
        this._contourThickness = 0.5;
        this.renderOptions = { fillAlpha: 0.25 };

        if (!viewportBoundingBox.bounds) {
            throw new Error(
                `Cannot create DrawObject: viewport bounding box bounds not defined.`
            );
        }

        // Snap the viewport box's voxel-space bounds outward to integer
        // voxel boundaries so the DrawObject grid is a true sub-region of
        // the image grid.
        const imageGrid = target.grid;
        const { min, max } = viewportBoundingBox.bounds;
        const voxelBounds = gridVoxelBounds(imageGrid, {
            minX: min[0],
            minY: min[1],
            minZ: min[2],
            maxX: max[0],
            maxY: max[1],
            maxZ: max[2],
        });
        const snappedMin = vec3.fromValues(
            Math.floor(voxelBounds.minX),
            Math.floor(voxelBounds.minY),
            Math.floor(voxelBounds.minZ)
        );
        const snappedSize = vec3.fromValues(
            Math.ceil(voxelBounds.maxX) - snappedMin[0],
            Math.ceil(voxelBounds.maxY) - snappedMin[1],
            Math.ceil(voxelBounds.maxZ) - snappedMin[2]
        );

        this._planarPixelSize = vec2.fromValues(snappedSize[0], snappedSize[1]);

        this._contourTexture = new ContourTexture(
            this._imageGrid,
            snappedSize,
            imageGrid.toWorldSpace(snappedMin)
        );

        this._color = [color[0], color[1], color[2]];
    }

    public get grid(): Grid {
        return this._contourTexture!.textureGrid;
    }

    /**
     * Method to get the {@link ReadonlyVec2 size} of this {@link DrawObject} in pixels along x and y -axes.
     */
    public get planarPixelSize(): ReadonlyVec2 {
        return this._planarPixelSize;
    }

    public toContourTexture(): ContourTexture | undefined {
        const sdf = this._contourTexture!.signedDistanceField;
        const bounds = sdf.getContentBounds();
        if (!bounds) {
            return undefined;
        }

        const { min, max } = bounds;
        // Content bounds are inclusive voxel indices on every axis.
        const sourceRowLen = max[0] - min[0] + 1;
        const sdfBytes = sdf.pixels;

        const marginSlices = 1;
        // add empty slices on top and bottom
        const sdfPixelSize = vec3.fromValues(
            sourceRowLen,
            max[1] - min[1] + 1,
            max[2] - min[2] + 1 + 2 * marginSlices
        );

        // world space coordinates of the corner of the SDF,
        // accounting for the margin slice below
        const cornerWorld = this.grid.toWorldSpace(
            vec3.fromValues(min[0], min[1], min[2] - marginSlices)
        );

        const contourTexture = new ContourTexture(
            this._targetImage.grid,
            sdfPixelSize,
            cornerWorld
        );

        const targetSdf = contourTexture.signedDistanceField;

        // copy the cropped SDF to the new SDF, offsetting by 1 to account for the margins
        const sliceArray = new Uint8Array(sdfPixelSize[0] * sdfPixelSize[1]);
        for (let z = min[2]; z <= max[2]; z++) {
            let targetIdx = 0;
            let sourceIdx = min[0] + min[1] * sdf.size[0];
            for (let y = min[1]; y <= max[1]; y++) {
                sliceArray.set(
                    sdfBytes[z].subarray(sourceIdx, sourceIdx + sourceRowLen),
                    targetIdx
                );
                targetIdx += sdfPixelSize[0];
                sourceIdx += sdf.size[0];
            }
            targetSdf.arrayToTextureSlice(sliceArray, z - min[2] + 1);
        }

        return contourTexture;
    }

    /**
     * Method to get the {@link SignedDistanceField} of this {@link DrawObject}.\
     * This is the {@link SignedDistanceField} on which the {@link DrawObject} is drawn.
     */
    public get signedDistanceField(): Readonly<SignedDistanceField> {
        return this._contourTexture!.signedDistanceField;
    }
}
